import { Widget } from '../core/Widget.js';
import { AirDatepickerWidget, RANGE_DASHES } from './AirDatepickerWidget.js';
import { getMainValueStr, setMainValue } from '../core/EncodeValues.js';
import { Loc, getInputFormats } from '../locales/registry.js';

/**
 * Виджет ввода архивной даты без jQuery.
 * Поведение повторяет $.widget("ui.imArchivalDate") из legacy: автоматический подбор маски по мере ввода,
 * смена типа, валидация, подключение календаря для точной даты.
 */
class ArchivalDateWidget extends Widget {
    constructor() {
        super(...arguments);
        // для логики переключения типов при вводе
        this._prevType = 0;
        // ссылки на подключённые вспомогательные виджеты
        this._datepicker = null;
        this.unbinders = [];
    }
    static _cssKey() { return "im-archivaldate"; }
    _create(op) {
        this.typeEl = op.typeControl ?? this.element.nextElementSibling;
        if (!this.typeEl)
            throw new Error("[ArchivalDateWidget] type control not found (nextElementSibling or options.typeControl)");
        this.locale = Loc;
        this.inputFormats = getInputFormats(op.lang);
        // начальная инициализация типа
        let type = this._readType();
        if (this._isCalendarOnly()) {
            type = "4";
            this._writeType(type);
        }
        if (!type) {
            // если кастомный dropdown: создаём placeholder сразу, чтобы было как в legacy
            if (this._isDropdown())
                this._ensurePlaceholder();
            type = "8"; // год по умолчанию
            this._writeType(type);
        }
        else {
            if (this._isDropdown())
                this._ensurePlaceholder();
        }
        // Первичный прогон
        this._onDateTypeChange(type, /*init*/ true);
        // Слушатели поля
        const onKeyDown = (e) => this._onKeyDown(e);
        const onKeyUp = (e) => this._onKeyUp(e);
        const onChange = () => {
            const t = this._readType();
            if (t)
                this._onArchivalDateChange(t);
        };
        const onInput = () => {
            this._autoDetectTypeByText();
            this._postNormalize();
        };
        this.element.addEventListener("keydown", onKeyDown);
        this.element.addEventListener("keyup", onKeyUp);
        this.element.addEventListener("change", onChange);
        this.element.addEventListener("input", onInput);
        this.unbinders.push(() => this.element.removeEventListener("keydown", onKeyDown));
        this.unbinders.push(() => this.element.removeEventListener("keyup", onKeyUp));
        this.unbinders.push(() => this.element.removeEventListener("change", onChange));
        this.unbinders.push(() => this.element.removeEventListener("input", onInput));
        // Слушатели изменения типа
        if (this._isDropdown()) {
            const el = this.typeEl;
            const onDdChange = () => {
                this._setPlaceholder();
                const t = this._readType();
                if (t)
                    this._onDateTypeChange(t);
            };
            el.addEventListener("ddchange", onDdChange);
            this.unbinders.push(() => el.removeEventListener("ddchange", onDdChange));
        }
        else {
            const sel = this.typeEl;
            const onSelChange = () => {
                const t = this._readType();
                if (t)
                    this._onDateTypeChange(t);
            };
            sel.addEventListener("change", onSelChange);
            this.unbinders.push(() => sel.removeEventListener("change", onSelChange));
        }
    }
    // ======== Публичное API совместимое с legacy ========
    /** Программно сменить тип (как legacy changeType) */
    changeType(val) {
        if (this._isCalendarOnly() && val !== "4")
            return;
        const current = this._readType();
        if (current === val)
            return;
        this._writeType(val);
        this._onDateTypeChange(val, undefined, true);
    }
    /**
     * Возвращает значение в серверном формате:
     * 4  → yyyy.MM.dd
     * 14 → yyyy.MM.dd-yyyy.MM.dd
     * остальные → как есть
     */
    getValue() {
        const t = this._readType();
        if (!t)
            return null;
        // ---- Точная дата ----
        // 2026-05-29: type=4 берём значение из datepicker, а не из element.value —
        // selectDate({silent:true}) не обновляет element.value синхронно
        if (t === "4") {
            if (!this._datepicker)
                return null;
            const d = this._datepicker.getValue();
            if (d instanceof Date)
                return d;
            // При ручном вводе тип переключается на точную дату раньше,
            // чем datepicker успевает синхронизировать внутреннее значение.
            // Валидный текст уже является полным пользовательским вводом и
            // не должен превращаться в null при немедленной отправке формы.
            return this._safeParseDateFromText(this.element.value?.trim() ?? "");
        }
        const raw = this.element.value?.trim();
        if (!raw)
            return null;
        // ---- Интервал дат ----
        if (t === "14") {
            if (!this._datepicker)
                return null;
            const values = this._datepicker.getValues();
            if (!values)
                return null;
            const key = this.element.dataset.key;
            const a = values[key];
            const b = values[`${key}__out`];
            if (a && b)
                return `${this._toServerDate(a)}-${this._toServerDate(b)}`;
            return null;
        }
        // ---- Текстовые типы ----
        return raw;
    }
    /** Форматирование Date → yyyy.MM.dd */
    _toServerDate(d) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}.${m}.${day}`;
    }
    /** Парсинг yyyy.MM.dd → Date */
    _fromServerDate(s) {
        const m = s.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
        if (!m)
            return null;
        const y = +m[1], mo = +m[2], d = +m[3];
        const date = new Date(y, mo - 1, d);
        if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d)
            return null;
        return date;
    }
    /** Парсинг серверного диапазона "yyyy.MM.dd-yyyy.MM.dd" */
    _parseServerRange(s) {
        const m = s.trim().match(/^(\d{4}\.\d{2}\.\d{2})\s*-\s*(\d{4}\.\d{2}\.\d{2})$/);
        if (!m)
            return null;
        const a = this._fromServerDate(m[1]);
        const b = this._fromServerDate(m[2]);
        if (!a || !b)
            return null;
        return [a, b];
    }
    /**
     * Программно установить значение.
     * @param value Строка, Date (для типа "4") или null/"" для очистки
     */
    setValue(value) {
        // Текущий (или только что выбранный) тип:
        const t = this._readType() ?? "8";
        // Очистка
        if (value == null || value === "") {
            const key = this.element.dataset.key;
            // setValues работает в обоих режимах: non-range игнорирует __out, range очищает обе границы
            this._datepicker?.setValues({
                [key]: null,
                [`${key}__out`]: null
            });
            this.element.value = "";
            this.element.classList.remove("input-validation-error");
            return true;
        }
        // Тип 4: допускаем Date и строку
        if (t === "4") {
            if (!this._datepicker)
                this._onDateTypeChange("4", undefined, true);
            if (value instanceof Date) {
                this._datepicker?.setValue(value);
                return true;
            }
            if (typeof value === "string") {
                const d = this._fromServerDate(value.trim());
                this._datepicker?.setValue(d ?? null);
                return true;
            }
        }
        // Тип 14: интервал дней
        if (t === "14") {
            if (!this._datepicker)
                this._onDateTypeChange("14", undefined, true);
            if (typeof value === "string") {
                const range = this._parseServerRange(value);
                const key = this.element.dataset.key;
                if (range) {
                    const [a, b] = range;
                    this._datepicker?.setValues({
                        [key]: a,
                        [`${key}__out`]: b
                    });
                }
                else {
                    // невалидная серверная строка — чистим состояние, чтобы не оставить stale range
                    this._datepicker?.setValues({
                        [key]: null,
                        [`${key}__out`]: null
                    });
                    this.element.value = "";
                    this.element.classList.remove("input-validation-error");
                }
                return true;
            }
        }
        // Строковые значения для всех типов
        if (typeof value === "string") {
            this.element.value = value;
            // Дадим шанс автонормализации/валидации для текущего типа
            this._onArchivalDateChange(t);
            return true;
        }
        // На всякий случай: неизвестный формат — очистим
        this.element.value = "";
        this.element.classList.remove("input-validation-error");
        return true;
    }
    /** Есть ли непустое значение в поле (для любого типа) */
    hasValue() {
        const v = this.element.value ?? "";
        return v.trim().length > 0;
    }
    // ======== Служебное ========
    _isDropdown() {
        const el = this.typeEl;
        return !!el && el.classList?.contains("dropdown");
    }
    _isCalendarOnly() {
        return this.element.dataset.mode === "calendar";
    }
    _readType() {
        const v = getMainValueStr(this.typeEl);
        return this._isType(v) ? v : null;
    }
    _writeType(t) {
        setMainValue(this.typeEl, t);
        this._setPlaceholder();
    }
    _isType(v) {
        return v === "4" || v === "6" || v === "8" || v === "10" || v === "12" || v === "14";
    }
    _ensurePlaceholder() {
        const dd = this.typeEl;
        if (!dd.querySelector(".archival-placeholder")) {
            const div = document.createElement("div");
            div.className = "archival-placeholder";
            dd.appendChild(div);
        }
        this._setPlaceholder();
    }
    _setPlaceholder() {
        if (!this._isDropdown())
            return;
        const dd = this.typeEl;
        const t = this._readType();
        const div = dd.querySelector(".archival-placeholder");
        if (!div || !t)
            return;
        let txt = "";
        const locale = this.locale;
        switch (t) {
            case "12":
                txt = locale.archivalDateYearRangePlaceholder;
                break;
            case "10":
                txt = locale.archivalDateDecadePlaceholder;
                break;
            case "8":
                txt = locale.archivalDateYearPlaceholder;
                break;
            case "6":
                txt = locale.archivalDateMonthPlaceholder;
                break;
            case "4":
                txt = locale.archivalDateDayPlaceholder;
                break;
            case "14":
                txt = locale.archivalDateDayRangePlaceholder;
                break;
        }
        div.textContent = txt;
    }
    _onKeyDown(e) {
        const el = this.element;
        const key = e.key; // уже нормализованная строка, напр. "1", "Backspace", "-"
        // ✅ 0) если активен datepicker-режим (4/14) — НЕ лезем в ввод
        // оставляем только спец-кейсы, которые реально нужны.
        const t = this._readType();
        const dpActive = (t === "4" || t === "14") && !!this._datepicker;
        // 2026-05-29: защита от двойной точки — РАНЬШЕ dpActive, т.к. в dp-режиме тоже не должны дублировать
        if ((key === "." || key === "," || key === "/" || key === "\\") && el.value.endsWith(".")) {
            e.preventDefault();
            return;
        }
        // ---- ИНТЕРВАЛ ДНЕЙ: если в точной дате нажали '-' ----
        if (t === "4" && key === "-") {
            const v = (el.value ?? "").trim();
            const looksLikeDate = /^\d{2}[.\-\\/]\d{2}[.\-\\/]\d{4}$/.test(v) || // dd.MM.yyyy etc
                /^\d{4}[.\-\\/]\d{2}[.\-\\/]\d{2}$/.test(v); // yyyy.MM.dd etc (если такое ещё допускаешь)
            // чтобы не ломать редактирование в середине строки
            const atEnd = (el.selectionStart ?? v.length) === (el.selectionEnd ?? v.length)
                && (el.selectionEnd ?? v.length) === (el.value ?? "").length;
            if (looksLikeDate && atEnd) {
                e.preventDefault();
                this.changeType("14"); // ✅ только переключаем режим
                return;
            }
        }
        // Ctrl+A в dpActive: явно выставляем выделение, потому что попап dp
        // может сбросить cursor-position до момента следующего Backspace-keydown
        if (dpActive && (e.ctrlKey || e.metaKey) && (e.code === "KeyA" || key.toLowerCase() === "a")) {
            e.preventDefault();
            el.setSelectionRange(0, el.value.length);
            return;
        }
        // ✅ всё остальное в режимах 4/14 отдаём dp (маска/контроль ввода его)
        if (dpActive && key !== "Backspace" && key !== "Delete") {
            return;
        }
        // ---- ДЕСЯТИЛЕТИЕ: авто-дополнение при нажатии '-' ----
        if (t === "10" && key === "-") {
            const v = el.value ?? "";
            const letter = this.inputFormats.archivalDateDecadeSuffix;
            if (/^\d{3}-$/.test(v)) {
                // "yyy-"  →  "yyy0-<буква>"
                e.preventDefault();
                el.value = v.replace(/-$/, `0-${letter}`);
                this._dispatchInput();
                return;
            }
            if (/^\d{3}--$/.test(v)) {
                // "yyy--" (двойной дефис из-за дефолтного поведения) → исправляем тоже
                e.preventDefault();
                el.value = v.replace(/--$/, `0-${letter}`);
                this._dispatchInput();
                return;
            }
            if (/^\d{4}-$/.test(v)) {
                // "yyyy-" → дописываем только букву
                e.preventDefault();
                el.value = v + letter;
                this._dispatchInput();
                return;
            }
        }
        // цифры (включая Numpad)
        const isDigit = key.length === 1 && key >= "0" && key <= "9";
        if (isDigit) {
            if (this._prevType === "12")
                return;
            const res = this._parseText(el.value);
            if (res.type === "y") {
                this.changeType("6");
                if (!el.value.endsWith("."))
                    el.value += ".";
            }
            else if (res.type === "m") {
                this.changeType("4");
                if (!el.value.endsWith("."))
                    el.value += ".";
            }
            // тут preventDefault НЕ нужен — цифра должна вставиться
            return;
        }
        // разделители (любой из - , . / \ )
        const isSep = key === "-" || key === "," || key === "." || key === "/" || key === "\\";
        if (isSep) {
            const txt = el.value;
            // ✅ "человеческий" ввод dd.MM.yyyy: разрешаем и 1-ю и 2-ю точку (после dd и после MM)
            // + приводим / и \ к точке
            const sep = (key === "/" || key === "\\" || key === ",") ? "." : key;
            if (sep === "." || sep === "-") {
                const v0 = (el.value ?? "");
                // dd<sep>  -> dd.
                if (/^\d{2}$/.test(v0)) {
                    e.preventDefault();
                    if (t !== "4")
                        this.changeType("4");
                    el.value = v0 + ".";
                    this._dispatchInput();
                    return;
                }
                // dd.MM<sep> -> dd.MM.
                if (/^\d{2}\.\d{2}$/.test(v0)) {
                    e.preventDefault();
                    if (t !== "4")
                        this.changeType("4");
                    el.value = v0 + ".";
                    this._dispatchInput();
                    return;
                }
                // если нажали / или \ или , — не даём вставить их (будем дальше обрабатывать как точку)
                if (key === "/" || key === "\\" || key === ",") {
                    // дальше пойдёт обычная логика обработки разделителя, но браузерскую вставку глушим
                    e.preventDefault();
                    // не return — пусть ниже отработают проверки/автопереходы
                }
            }
            // два дефиса подряд не допускаем
            if (key === "-" && txt.endsWith("-")) {
                e.preventDefault();
                return;
            }
            // 🔧 защита от двойной точки: если уже есть хвостовая '.', не даём браузеру вставить ещё одну
            if (key === "." && txt.endsWith(".")) {
                e.preventDefault();
                return;
            }
            // повторный «разделитель-точка»: если на конце уже '.', то
            // любые попытки вставить ещё один разделитель-точку (.,/,\,,)
            // блокируем — чтобы не получить "2024..", "2024.," и т.п.
            if ((key === "." || key === "," || key === "/" || key === "\\") && txt.endsWith(".")) {
                e.preventDefault();
                return;
            }
            if (/^\d{3}$/.test(txt) && key === "-") {
                const dec = txt + "0-" + this.inputFormats.archivalDateDecadeSuffix;
                e.preventDefault();
                el.value = dec;
                this.changeType("10");
                this._dispatchInput();
                return;
            }
            const res = this._parseText(txt);
            if (res.type === "y") {
                if (key === "-") {
                    // год → интервал: вставляем дефис САМИ и гасим дефолт, чтобы избежать гонок со сменой типа
                    e.preventDefault();
                    if (!el.value.endsWith("-"))
                        el.value += "-";
                    this.changeType("12");
                    this._dispatchInput();
                    return;
                }
                else {
                    e.preventDefault();
                    const yearPart = el.value.substring(0, 4);
                    this.changeType("6");
                    el.value = yearPart + ".";
                    this._dispatchInput();
                    return;
                }
            }
            else if (res.type === "m") {
                this.changeType("4");
                if (key !== "-") {
                    e.preventDefault();
                    if (!el.value.endsWith("."))
                        el.value += ".";
                    this._dispatchInput();
                }
            }
            return;
        }
        // Backspace / Delete
        if (key === "Backspace" || key === "Delete") {
            e.preventDefault();
            const before = el.value ?? "";
            if (!before)
                return;
            const start = el.selectionStart ?? before.length;
            const end = el.selectionEnd ?? before.length;
            let after;
            // ✅ если есть выделение — удаляем выделенный диапазон
            if (start !== end) {
                after = before.slice(0, start) + before.slice(end);
                el.value = after;
                el.setSelectionRange(start, start);
            }
            else {
                // ✅ иначе удаляем 1 символ слева (Backspace) или справа (Delete)
                if (key === "Backspace") {
                    if (start <= 0)
                        return;
                    after = before.slice(0, start - 1) + before.slice(end);
                    el.value = after;
                    el.setSelectionRange(start - 1, start - 1);
                }
                else {
                    // Delete
                    if (start >= before.length)
                        return;
                    after = before.slice(0, start) + before.slice(start + 1);
                    el.value = after;
                    el.setSelectionRange(start, start);
                }
            }
            // ✅ спец-случай: в '6' оставляем тип, если получился недописанный месяц
            if (this._prevType === "6" &&
                (/^\d{4}\.$/.test(el.value) || /^\d{4}\.\d$/.test(el.value))) {
                return;
            }
            // ✅ дальше — твоя логика автосмены типов
            const res = this._parseText(el.value);
            if (res.type === "y") {
                if (el.value.endsWith("."))
                    this.changeType("6");
                else
                    this.changeType("8");
            }
            else if (res.type === "m") {
                if (this._prevType !== "12")
                    this.changeType("6");
            }
            else if (el.value === "" && this._prevType !== "4") {
                this.changeType("8");
            }
            this._dispatchInput();
            return;
        }
        // прочие клавиши (стрелки, таб, home/end и т.п.) игнорируем — пусть проходят
    }
    _onKeyUp(e) {
        // Резервная ветка: если keydown не перехватил (например, значение очистили программно),
        // проверяем при keyup — пустое поле → возвращаем тип "год"
        if (e.key === "Backspace" && this.element.value === "" && this._prevType !== "4") {
            this.changeType("8");
        }
    }
    _onDateTypeChange(type, init, skipValidate) {
        if (this._isCalendarOnly() && type !== "4") {
            type = "4";
            this._writeType(type);
        }
        const txtEl = this.element;
        const val = txtEl.value ?? "";
        // 1) Если dp был — всегда уничтожаем при смене типа
        if (this._datepicker || txtEl.classList.contains("__dt")) {
            this._destroyDatepicker();
        }
        // пересчитать значение при смене типа
        this._updateValFromPrevious(type, !!init, val);
        // 3) Подключаем dp ТОЛЬКО для 4 и 14
        if (type === "4") {
            this._datepicker = AirDatepickerWidget.getOrInit(txtEl);
            txtEl.classList.add("__dt");
        }
        else if (type === "14") {
            this._datepicker = AirDatepickerWidget.getOrInit(txtEl, {
                range: true
            });
            txtEl.classList.add("__dt");
        }
        this._prevType = type;
        if (!skipValidate)
            this._onArchivalDateChange(type);
    }
    /**
 * Если dp активен, но текст больше не соответствует "режиму даты",
 * отключаем dp и переводим виджет в текстовый режим (6/8/10/12).
 */
    _maybeDetachPickerByText() {
        const t = this._readType();
        if (t !== "4" && t !== "14")
            return;
        if (!this._datepicker)
            return;
        const v = (this.element.value ?? "").trim();
        if (!v)
            return;
        // 1) если явно стало "yyyy" / "yyyy.MM" / "yyyy-yyyy" / "yyy0-s"
        const p = this._parseText(v);
        if (p.type === "y") {
            this.changeType("8");
            return;
        }
        if (p.type === "m") {
            this.changeType("6");
            return;
        }
        if (p.type === "yy") {
            this.changeType("12");
            return;
        }
        if (p.type === "dk") {
            this.changeType("10");
            return;
        }
        // 2) если мы в range, но диапазона больше нет — можно вернуться в single
        if (t === "14") {
            const looksLikeSingle = /^\d{2}[.\-\\/]\d{2}[.\-\\/]\d{4}$/.test(v) || // dd.MM.yyyy etc
                /^\d{4}[.\-\\/]\d{2}[.\-\\/]\d{2}$/.test(v); // yyyy.MM.dd etc
            if (looksLikeSingle) {
                this.changeType("4");
                return;
            }
        }
        // 3) если мы в single, но пользователь начал вводить диапазон лет/десятилетие — detatch уже выше поймано
        // остальное — оставляем dp, он сам разберёт валидность и откат на blur.
    }
    /**
     * Пересчитывает текстовое значение при смене типа.
     * Если предыдущий тип был "4" (день), то приоритетно берём Date из AirDatepicker
     * (он уже умеет парсить локальный ввод), и только если его нет — падаем на строковый парсер.
     */
    _updateValFromPrevious(type, init, val) {
        const txt = this.element;
        /** Берём выбранную дату из datepicker (для типа "4"), если она есть */
        const dpDate = () => {
            const v = this._datepicker?.getValue();
            return v instanceof Date ? v : null;
        };
        /** Берём дату: сперва из dp, иначе — из текста (yyyy.MM.dd или dd.MM.yyyy) */
        const anyDate = () => dpDate() ?? this._safeParseDateFromText(val);
        try {
            switch (type) {
                case "12": { // yyyy-yyyy
                    if (this._prevType === "4") {
                        const d = anyDate();
                        if (d) {
                            const y = String(d.getFullYear());
                            txt.value = `${y}-${y}`;
                        }
                        else if (!init) {
                            txt.value = "";
                        }
                    }
                    else if ((this._prevType === "6" || this._prevType === "8") && val.length >= 4) {
                        // Если val уже полный диапазон — сохраняем как есть
                        const dashClass = RANGE_DASHES.replace(/-/g, "\\-");
                        const yearRange = new RegExp(`^(\\d{4})[${dashClass}](\\d{4})$`).exec(val);
                        if (yearRange) {
                            txt.value = `${yearRange[1]}-${yearRange[2]}`;
                        }
                        else if (val.endsWith("-")) {
                            // пользователь уже ввёл дефис вручную — не перетираем частичный ввод
                            txt.value = val;
                        }
                        else {
                            txt.value = `${val.substring(0, 4)}-${val.substring(0, 4)}`;
                        }
                    }
                    else if (this._prevType !== "12") {
                        if (!init)
                            txt.value = "";
                    }
                    break;
                }
                case "10": { // yyy0-s / yyy0-е
                    if (this._prevType === "4") {
                        const d = anyDate();
                        if (d) {
                            const y = String(d.getFullYear());
                            txt.value = `${y.slice(0, 3)}0-`;
                        }
                        else if (!init) {
                            txt.value = "";
                        }
                    }
                    else if ((this._prevType === "6" || this._prevType === "8" || this._prevType === "12") && val.length >= 3) {
                        txt.value = `${val.substring(0, 3)}0-`;
                    }
                    else if (this._prevType !== "10") {
                        if (!init)
                            txt.value = "";
                    }
                    break;
                }
                case "8": { // yyyy
                    if (this._prevType === "4") {
                        const d = anyDate();
                        if (d)
                            txt.value = String(d.getFullYear());
                        else if (!init)
                            txt.value = "";
                    }
                    else if ((this._prevType === "6" || this._prevType === "12") && val.length >= 4) {
                        txt.value = val.substring(0, 4);
                    }
                    else if (this._prevType === "10" && val.length >= 3) {
                        txt.value = val.substring(0, 3) + "0";
                    }
                    else {
                        // страховка: если уже видим год в начале — не очищаем
                        if (!init) {
                            if (/^\d{4}/.test(val))
                                txt.value = val.substring(0, 4);
                            else
                                txt.value = "";
                        }
                    }
                    break;
                }
                case "6": { // yyyy.MM
                    if (this._prevType === "4") {
                        const d = anyDate();
                        if (d) {
                            const y = d.getFullYear();
                            const m = d.getMonth() + 1; // 1..12
                            txt.value = `${y}.${String(m).padStart(2, "0")}`;
                        }
                        else {
                            // dp не выбрал дату — сохраняем val, если он уже выглядит как yyyy.MM
                            const mMatch = val.match(/^(\d{4})[.\-\\/](\d{2})$/);
                            if (mMatch) {
                                txt.value = `${mMatch[1]}.${mMatch[2]}`;
                            }
                            else if (!init) {
                                txt.value = "";
                            }
                        }
                    }
                    else if (this._prevType === "8" || this._prevType === "12") {
                        if (val.length >= 4) {
                            // Если val уже содержит месяц (пасте / автодетект) — сохраняем его
                            const mMatch = val.match(/^\d{4}[.\-\\/](\d{1,2})$/);
                            if (mMatch) {
                                txt.value = `${val.substring(0, 4)}.${String(Number(mMatch[1])).padStart(2, "0")}`;
                            }
                            else {
                                // по умолчанию подставляем январь; при вводе с клавиатуры
                                // _onKeyDown-сепаратор перебивает значение на "yyyy."
                                txt.value = `${val.substring(0, 4)}.01`;
                            }
                        }
                        else if (!init)
                            txt.value = "";
                    }
                    else if (this._prevType === "6") {
                        if (val.length === 4)
                            txt.value = val + ".";
                    }
                    else {
                        if (!init)
                            txt.value = "";
                    }
                    break;
                }
                case "4": { // dd.MM.yyyy / MM.dd.yyyy (display) — dp сам разрулит
                    if (this._prevType === "14") {
                        const range = this._splitRange(val);
                        if (range) {
                            const d = this._safeParseDateFromText(range[0]);
                            if (d) {
                                const dd = String(d.getDate()).padStart(2, "0");
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const yyyy = d.getFullYear();
                                txt.value = `${dd}.${mm}.${yyyy}`;
                            }
                            else if (!init) {
                                txt.value = "";
                            }
                        }
                        else if (!init) {
                            txt.value = "";
                        }
                        break;
                    }
                    // если пришли из "6"/"8"/"12" — не пытаемся достраивать руками;
                    // пользователь выберет/введёт дату, dp распарсит на blur.
                    break;
                }
                case "14": {
                    const v = (val ?? "").trim();
                    // если уже диапазон — не трогаем
                    if (this._splitRange(v)) {
                        txt.value = val;
                        break;
                    }
                    // если пользователь уже начал ввод диапазона (дата + тире в конце) — не трогаем
                    if (/(\d{4}[.\-\\/]\d{2}[.\-\\/]\d{2}|\d{2}[.\-\\/]\d{2}[.\-\\/]\d{4})\s*[–—-]\s*$/.test(v)) {
                        txt.value = val;
                        break;
                    }
                    // ✅ пришли из "4": подставляем только старт и " - ", без автозаполнения конца
                    if (this._prevType === "4") {
                        const d = anyDate();
                        if (d) {
                            const dd = String(d.getDate()).padStart(2, "0");
                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                            const yyyy = d.getFullYear();
                            const one = `${dd}.${mm}.${yyyy}`;
                            txt.value = one;
                            break;
                        }
                    }
                    if (this._prevType !== "14") {
                        if (!init)
                            txt.value = "";
                    }
                    break;
                }
            }
        }
        catch (e) {
            console.error(e);
            if (!init)
                txt.value = "";
        }
    }
    _splitRange(txt) {
        const s = (txt ?? "").trim();
        if (!s)
            return null;
        const dashClass = RANGE_DASHES.replace(/-/g, "\\-");
        // Явно матчим оба конца как известные форматы дат:
        // yyyy.MM.dd / yyyy-MM-dd / yyyy/MM/dd и dd.MM.yyyy / dd-MM-yyyy и т.д.
        // Это исключает ложные срабатывания на дефисы внутри ISO-дат.
        const datePat = String.raw `\d{4}[.\-\\/]\d{2}[.\-\\/]\d{2}|\d{2}[.\-\\/]\d{2}[.\-\\/]\d{4}`;
        const re = new RegExp(`^(${datePat})\\s*[${dashClass}]\\s*(${datePat})$`);
        const m = s.match(re);
        if (!m)
            return null;
        return [(m[1] ?? "").trim(), (m[2] ?? "").trim()];
    }
    _postNormalize() {
        // 1) пост-нормализация десятилетия
        if (this._readType() === "10") {
            const symb = this.inputFormats.archivalDateDecadeSuffix;
            const v = this.element.value ?? "";
            if (/^\d{3}-$/.test(v)) {
                this.element.value = v.replace(/-$/, `0-${symb}`);
            }
            else if (/^\d{3}--$/.test(v)) {
                this.element.value = v.replace(/--$/, `0-${symb}`);
            }
            else if (/^\d{4}-$/.test(v)) {
                this.element.value = v + symb;
            }
        }
        // 2) а вот detatch dp должен быть доступен ВСЕГДА
        this._maybeDetachPickerByText();
    }
    _onArchivalDateChange(type) {
        try {
            // работаем с тем, что реально в инпуте
            let txt = (this.element.value ?? "").trim();
            // пусто = валидно (обычно так), ошибку снимаем
            if (!txt) {
                this.element.classList.remove("input-validation-error");
                return;
            }
            // ✅ 1) автодополнение десятилетия (единственное место где мы "правим" текст)
            if (type === "10") {
                const symb = this.inputFormats.archivalDateDecadeSuffix;
                // yyy-  → yyy0-<буква>
                if (/^\d{3}-$/.test(txt)) {
                    txt = txt.replace(/-$/, `0-${symb}`);
                }
                // yyy-- → yyy0-<буква>
                else if (/^\d{3}--$/.test(txt)) {
                    txt = txt.replace(/--$/, `0-${symb}`);
                }
                // yyyy- → yyyy-<буква>
                else if (/^\d{4}-$/.test(txt)) {
                    txt = txt + symb;
                }
                if (txt !== this.element.value)
                    this.element.value = txt;
                txt = (this.element.value ?? "").trim();
                if (!txt) {
                    this.element.classList.remove("input-validation-error");
                    return;
                }
            }
            // ✅ 2) валидация — dp-режимы валидируем ТОЛЬКО через dp
            let isValid = false;
            switch (type) {
                case "14":
                    // Календарь сам занимается ошибками для интервала дней
                    return;
                case "4": {
                    // 2026-05-29: dp валидирует при выборе, но при ручном вводе текстовая проверка нужна здесь
                    const d4 = this._safeParseDateFromText(txt);
                    if (d4)
                        this.element.classList.remove("input-validation-error");
                    else
                        this.element.classList.add("input-validation-error");
                    return;
                }
                case "12": {
                    // yyyy-yyyy (простая проверка)
                    // можно оставить через parseText, но тут быстрее и прозрачнее
                    const dashClass = RANGE_DASHES.replace(/-/g, "\\-");
                    const yearRange = new RegExp(`^(\\d{4})[${dashClass}](\\d{4})$`).exec(txt);
                    if (yearRange) {
                        const a = yearRange[1];
                        const b = yearRange[2];
                        const fixed = `${a}-${b}`;
                        if (fixed !== this.element.value)
                            this.element.value = fixed;
                        isValid = a <= b;
                    }
                    else {
                        isValid = false;
                    }
                    break;
                }
                case "10": {
                    // строго yyy0-<буква> с правильной буквой по языку
                    const symb = this.inputFormats.archivalDateDecadeSuffix;
                    isValid = /^\d{3}0-[s\u0435]$/i.test(txt) && txt.endsWith(symb);
                    break;
                }
                case "8": {
                    isValid = /^\d{4}$/.test(txt);
                    break;
                }
                case "6": {
                    // yyyy.MM (месяц 01..12), разрешим ввод с любым разделителем, но нормализуем на '.'
                    if (/^\d{4}[.\-\\/]\d{2}$/.test(txt)) {
                        let fixed = txt.replace(/[\/\\\-]/g, ".");
                        const mm = fixed.slice(-2);
                        if (mm === "00")
                            fixed = fixed.slice(0, 5) + "01";
                        else if (Number(mm) > 12)
                            fixed = fixed.slice(0, 5) + "12";
                        if (fixed !== this.element.value)
                            this.element.value = fixed;
                        isValid = /^\d{4}\.(0[1-9]|1[0-2])$/.test(fixed);
                    }
                    else {
                        isValid = false;
                    }
                    break;
                }
            }
            if (isValid)
                this.element.classList.remove("input-validation-error");
            else
                this.element.classList.add("input-validation-error");
        }
        catch (e) {
            console.error("ArchivalDateWidget._onArchivalDateChange", e);
            this.element.classList.add("input-validation-error");
        }
    }
    _parseText(txt) {
        // 2026-05-29: намеренно НЕ делаем trim — это ответственность вызывающего кода
        const s = txt ?? "";
        const dashClass = RANGE_DASHES.replace(/-/g, "\\-");
        // 1) интервал дней: (yyyy.MM.dd | dd.MM.yyyy) [RANGE_DASHES] (yyyy.MM.dd | dd.MM.yyyy)
        const dayRange = new RegExp(`^(\\d{4}[.\\-\\\\/]\\d{2}[.\\-\\\\/]\\d{2}|\\d{2}[.\\-\\\\/]\\d{2}[.\\-\\\\/]\\d{4})\\s*[${dashClass}]\\s*(\\d{4}[.\\-\\\\/]\\d{2}[.\\-\\\\/]\\d{2}|\\d{2}[.\\-\\\\/]\\d{2}[.\\-\\\\/]\\d{4})\\??$`, "i");
        if (dayRange.test(s)) {
            return { type: 'dd', txt: s, valid: true };
        }
        // 2) десятилетие: yyy0-s / yyy0-е (с/без дефиса)
        if (/^\d{3}0-?[s\u0435]\??$/i.test(s)) {
            return { type: 'dk', txt: s.replace(/\?$/, ""), valid: true };
        }
        // 3) yyyy-yyyy
        const yearRange = new RegExp(`^(\\d{4})[${dashClass}](\\d{4})\\??$`, "i").exec(s);
        if (yearRange) {
            const a = yearRange[1];
            const b = yearRange[2];
            return { type: 'yy', txt: `${a}-${b}`, valid: a <= b };
        }
        // 4) yyyy.MM.dd (одиночная дата) — это НЕ формат для этого парсера (как у тебя было)
        if (/^\d{4}[.\-\\/]\d{2}[.\-\\/]\d{2}\??$/i.test(s)) {
            return { type: '?', txt: s };
        }
        // 5) yyyy.MM
        if (/^\d{4}[.\-\\/]\d{2}\??$/i.test(s)) {
            return { type: 'm', txt: s, valid: true };
        }
        // 6) yyyy
        if (/^\d{4}\??$/.test(s)) {
            return { type: 'y', txt: s, valid: true };
        }
        return { type: '?', txt: s };
    }
    _safeParseDateFromText(txt) {
        // 1) Жёстко отрезаем слитные 8 цифр (YYYYMMDD)
        if (/^\d{8}$/.test(txt))
            return null;
        const s = txt.trim();
        // yyyy.MM.dd | yyyy-MM-dd | yyyy/MM/dd | yyyy\MM\dd
        let m = s.match(/^(\d{4})[.\-\\/](\d{2})[.\-\\/](\d{2})$/);
        if (m) {
            const y = Number(m[1]);
            const mo = Number(m[2]);
            const d = Number(m[3]);
            const date = new Date(y, mo - 1, d);
            if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d)
                return null;
            return date;
        }
        //TODO: Тут будут проблемы на английском
        // dd.MM.yyyy | dd-MM-yyyy | dd/MM/yyyy | dd\MM\yyyy  ✅ новый формат
        m = s.match(/^(\d{2})[.\-\\/](\d{2})[.\-\\/](\d{4})$/);
        if (m) {
            const d = Number(m[1]);
            const mo = Number(m[2]);
            const y = Number(m[3]);
            const date = new Date(y, mo - 1, d);
            if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d)
                return null;
            return date;
        }
        return null;
    }
    /**
 * Определяет тип по текущему тексту и переключает режим,
 * если тип отличается от текущего.
 */
    _autoDetectTypeByText() {
        const txt = (this.element.value ?? "").trim();
        if (!txt)
            return;
        const parsed = this._parseText(txt);
        const current = this._readType();
        let next = null;
        switch (parsed.type) {
            case "dd":
                next = "14";
                break; // интервал дней
            case "yy":
                next = "12";
                break; // интервал лет
            case "dk":
                next = "10";
                break; // десятилетие
            case "m":
                next = "6";
                break; // год+месяц
            case "y":
                next = "8";
                break; // год
        }
        if (next && next !== current) {
            // yyyy-{1..3 цифры} — незавершённый второй год интервала (2026-20xx),
            // а не год+месяц с дефисом в роли разделителя. Не переключаем в тип "6".
            if (next === "6" && /^\d{4}-\d{1,3}$/.test(txt)) ;
            else {
                this.changeType(next);
            }
        }
        // если это одиночная дата (yyyy.MM.dd или dd.MM.yyyy)
        if (parsed.type === "?" && this._safeParseDateFromText(txt)) {
            if (current !== "4") {
                this.changeType("4");
            }
        }
    }
    _dispatchInput() {
        this.element.dispatchEvent(new Event('input', { bubbles: true }));
    }
    /** Снимает календарь и его обработчики, сохраняя редактор архивной даты на том же поле. */
    _destroyDatepicker() {
        // Уничтожается конкретный календарь, а не все виджеты поля. | 2026-10-02
        this._datepicker?.destroy();
        this._datepicker = null;
        this.element.classList.remove("__dt");
    }
    _onDestroy() {
        this._destroyDatepicker();
        for (const u of this.unbinders) {
            try {
                u();
            }
            catch { }
        }
        this.unbinders.length = 0;
    }
}
/** Имя виджета */
ArchivalDateWidget._alias = "ArchivalDate";

export { ArchivalDateWidget };
